import { useState, useEffect } from 'react';
import { Radio, MapPin, Zap, Shield, Heart, Clock, ChevronDown, Activity, Eye, Sparkles } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Smooth scroll to section
const scrollToSection = (id) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
};

// Signal category colors
const getCategoryColor = (category) => {
  const colors = {
    'Weather': 'from-blue-500/20 to-blue-600/10 border-blue-500/30 text-blue-400',
    'Supply Issues': 'from-amber-500/20 to-amber-600/10 border-amber-500/30 text-amber-400',
    'Health': 'from-emerald-500/20 to-emerald-600/10 border-emerald-500/30 text-emerald-400',
    'Unrest': 'from-red-500/20 to-red-600/10 border-red-500/30 text-red-400',
    'Other': 'from-violet-500/20 to-violet-600/10 border-violet-500/30 text-violet-400',
  };
  return colors[category] || colors['Other'];
};

const getCategoryBadgeColor = (category) => {
  const colors = {
    'Weather': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    'Supply Issues': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    'Health': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    'Unrest': 'bg-red-500/20 text-red-400 border-red-500/30',
    'Other': 'bg-violet-500/20 text-violet-400 border-violet-500/30',
  };
  return colors[category] || colors['Other'];
};

// Format time ago
const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

// Live Signal Card for snapshot section
const LiveSignalCard = ({ signal, index }) => {
  const categoryColor = getCategoryColor(signal.category);
  const badgeColor = getCategoryBadgeColor(signal.category);
  
  return (
    <div 
      className={`relative bg-gradient-to-br ${categoryColor} backdrop-blur-sm border rounded-xl p-4 transform transition-all duration-500 hover:scale-[1.02] hover:shadow-lg hover:shadow-blue-500/5`}
      style={{ 
        animationDelay: `${index * 150}ms`,
        animation: 'fadeSlideIn 0.6s ease-out forwards',
        opacity: 0
      }}
      data-testid={`live-signal-${index}`}
    >
      {/* Live indicator */}
      <div className="absolute top-3 right-3 flex items-center gap-1.5">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
        </span>
        <span className="text-[10px] text-green-400 font-medium uppercase tracking-wider">Live</span>
      </div>

      {/* Category badge */}
      <div className="flex items-center gap-2 mb-3">
        <span className={`text-[11px] px-2.5 py-1 rounded-full border font-medium ${badgeColor}`}>
          {signal.category}
        </span>
      </div>

      {/* Description */}
      <p className="text-zinc-200 text-sm leading-relaxed mb-3 line-clamp-2">
        {signal.short_description}
      </p>

      {/* Location & Time */}
      <div className="flex items-center justify-between text-xs text-zinc-500">
        <div className="flex items-center gap-1">
          <MapPin className="w-3 h-3" />
          <span>{signal.city}, {signal.state}</span>
        </div>
        <div className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          <span>{formatTimeAgo(signal.created_date)}</span>
        </div>
      </div>
    </div>
  );
};

// Feature card component
const FeatureCard = ({ icon: Icon, title, description }) => (
  <div className="group relative bg-zinc-900/50 backdrop-blur-sm border border-zinc-800 rounded-xl p-5 transition-all duration-300 hover:border-zinc-700 hover:bg-zinc-900/80">
    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    <div className="relative">
      <div className="w-10 h-10 bg-zinc-800 rounded-lg flex items-center justify-center mb-4 group-hover:bg-zinc-700/80 transition-colors">
        <Icon className="w-5 h-5 text-zinc-300" />
      </div>
      <h3 className="text-zinc-100 font-semibold mb-2">{title}</h3>
      <p className="text-zinc-500 text-sm leading-relaxed">{description}</p>
    </div>
  </div>
);

// CTA Button component
const CTAButton = ({ children, onClick, variant = 'primary', size = 'default', className = '' }) => {
  const baseStyles = 'font-semibold rounded-xl transition-all duration-300 flex items-center justify-center gap-2';
  const variants = {
    primary: 'bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white shadow-lg shadow-red-500/25 hover:shadow-red-500/40 hover:scale-[1.02]',
    secondary: 'bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border border-zinc-700 hover:border-zinc-600',
    ghost: 'bg-transparent hover:bg-zinc-800/50 text-zinc-300 hover:text-zinc-100',
  };
  const sizes = {
    default: 'px-6 py-3 text-base',
    large: 'px-8 py-4 text-lg',
    small: 'px-4 py-2 text-sm',
  };

  return (
    <button 
      onClick={onClick}
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      data-testid="cta-button"
    >
      {children}
    </button>
  );
};

export const LandingPage = ({ onJoinClick, onSignInClick, onPatreonClick }) => {
  const [liveSignals, setLiveSignals] = useState([]);
  const [pulseCount, setPulseCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch live signals for the snapshot section
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [signalsRes, pulseRes] = await Promise.all([
          axios.get(`${API}/signals`),
          axios.get(`${API}/signals/pulse`)
        ]);
        setLiveSignals(signalsRes.data.slice(0, 5));
        setPulseCount(pulseRes.data.count);
      } catch (error) {
        console.error('Error fetching signals:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-[#050508] text-white overflow-x-hidden">
      {/* Custom styles for animations */}
      <style>{`
        @keyframes fadeSlideIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes pulse-ring {
          0% {
            transform: scale(0.8);
            opacity: 0.8;
          }
          100% {
            transform: scale(2);
            opacity: 0;
          }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .gradient-text {
          background: linear-gradient(135deg, #fff 0%, #a1a1aa 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .glow-red {
          box-shadow: 0 0 60px rgba(239, 68, 68, 0.3);
        }
      `}</style>

      {/* ===== SECTION 1: HERO ===== */}
      <section className="relative min-h-screen flex flex-col justify-center px-4 sm:px-6 overflow-hidden" data-testid="hero-section">
        {/* Background effects */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Gradient orbs */}
          <div className="absolute top-1/4 -left-20 w-96 h-96 bg-red-500/10 rounded-full blur-[120px] animate-float" />
          <div className="absolute bottom-1/4 -right-20 w-80 h-80 bg-blue-500/10 rounded-full blur-[100px] animate-float" style={{ animationDelay: '-3s' }} />
          
          {/* Grid pattern overlay */}
          <div 
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `
                linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)
              `,
              backgroundSize: '50px 50px'
            }}
          />
        </div>

        {/* Navigation */}
        <nav className="absolute top-0 left-0 right-0 z-50 px-4 sm:px-6 py-4">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-600/20 rounded-lg flex items-center justify-center border border-red-500/30">
                <Radio className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <span className="font-bold text-lg text-zinc-100 tracking-tight">InSpiteOf</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {onPatreonClick && (
                <button 
                  onClick={onPatreonClick}
                  className="text-amber-400 hover:text-amber-300 text-sm font-medium transition-colors px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-center gap-1.5"
                  data-testid="nav-patreon-btn"
                >
                  <Shield className="w-4 h-4" />
                  <span className="hidden sm:inline">Patreon</span>
                </button>
              )}
              <button 
                onClick={onSignInClick}
                className="text-zinc-400 hover:text-zinc-200 text-sm font-medium transition-colors px-4 py-2"
                data-testid="nav-signin-btn"
              >
                Sign In
              </button>
            </div>
          </div>
        </nav>

        {/* Hero content */}
        <div className="relative z-10 max-w-4xl mx-auto text-center pt-24 pb-16">
          {/* Live pulse indicator */}
          <div className="inline-flex items-center gap-2 bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-full px-4 py-2 mb-8" data-testid="live-pulse-indicator">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
            </span>
            <span className="text-sm text-zinc-400">
              <span className="text-red-400 font-semibold">{pulseCount}</span> signals in the last 24h
            </span>
          </div>

          {/* Main headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] mb-6 tracking-tight" data-testid="hero-headline">
            <span className="gradient-text">What's Happening Around You…</span>
            <br />
            <span className="text-zinc-100">Before It Becomes Headlines</span>
          </h1>

          {/* Subheadline */}
          <p className="text-base sm:text-lg text-zinc-400 max-w-2xl mx-auto mb-10 leading-relaxed" data-testid="hero-subheadline">
            Real-time signals from people across the country—weather anomalies, supply disruptions, unusual activity, and more.
          </p>

          {/* CTA buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <CTAButton onClick={onJoinClick} size="large" data-testid="hero-join-btn">
              Join the Network
            </CTAButton>
            <CTAButton 
              onClick={() => scrollToSection('live-snapshot')} 
              variant="secondary" 
              size="large"
              data-testid="hero-preview-btn"
            >
              <Eye className="w-4 h-4" />
              See Live Signals
            </CTAButton>
          </div>

          {/* Secondary text */}
          <p className="text-sm text-zinc-500">
            See what others are reporting right now
          </p>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <button 
            onClick={() => scrollToSection('what-is-this')}
            className="text-zinc-600 hover:text-zinc-400 transition-colors"
            aria-label="Scroll down"
          >
            <ChevronDown className="w-6 h-6" />
          </button>
        </div>
      </section>

      {/* ===== SECTION 2: WHAT THIS IS ===== */}
      <section id="what-is-this" className="py-20 sm:py-28 px-4 sm:px-6 bg-gradient-to-b from-[#050508] to-zinc-950" data-testid="what-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-zinc-100 mb-6">
              A Private Intelligence Network
            </h2>
            <p className="text-zinc-400 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
              The InSpiteOf Private Signal Network is a real-time awareness platform where people share what they're seeing in their area—<span className="text-zinc-200">before it hits the news.</span>
            </p>
          </div>

          {/* Bullet points as feature cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-start gap-3 bg-zinc-900/30 border border-zinc-800/50 rounded-lg p-4">
              <div className="w-8 h-8 bg-red-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Activity className="w-4 h-4 text-red-400" />
              </div>
              <div>
                <h3 className="text-zinc-200 font-medium mb-1">Live User-Submitted Signals</h3>
                <p className="text-zinc-500 text-sm">Real reports from real people on the ground</p>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-zinc-900/30 border border-zinc-800/50 rounded-lg p-4">
              <div className="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <MapPin className="w-4 h-4 text-blue-400" />
              </div>
              <div>
                <h3 className="text-zinc-200 font-medium mb-1">Nationwide Coverage</h3>
                <p className="text-zinc-500 text-sm">Signals from every state and major city</p>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-zinc-900/30 border border-zinc-800/50 rounded-lg p-4">
              <div className="w-8 h-8 bg-amber-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Zap className="w-4 h-4 text-amber-400" />
              </div>
              <div>
                <h3 className="text-zinc-200 font-medium mb-1">Multiple Categories</h3>
                <p className="text-zinc-500 text-sm">Weather, Supply, Health, Unrest & Other</p>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-zinc-900/30 border border-zinc-800/50 rounded-lg p-4">
              <div className="w-8 h-8 bg-emerald-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Clock className="w-4 h-4 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-zinc-200 font-medium mb-1">Updated Constantly</h3>
                <p className="text-zinc-500 text-sm">New signals flowing in 24/7</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 3: WHY IT MATTERS ===== */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 bg-zinc-950" data-testid="why-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-zinc-100 mb-6">
            Most People Find Out Too Late
          </h2>
          <p className="text-zinc-400 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            By the time something hits the news, it's already happening. This network gives you <span className="text-zinc-200 font-medium">early awareness</span> through real people on the ground—not algorithms, not clickbait, just honest observations.
          </p>
          
          {/* Visual separator */}
          <div className="flex items-center justify-center gap-3 mt-12">
            <div className="w-16 h-px bg-gradient-to-r from-transparent to-zinc-800" />
            <div className="w-2 h-2 rounded-full bg-red-500/50" />
            <div className="w-16 h-px bg-gradient-to-l from-transparent to-zinc-800" />
          </div>
        </div>
      </section>

      {/* ===== SECTION 4: LIVE SNAPSHOT (SOCIAL PROOF) ===== */}
      <section id="live-snapshot" className="py-20 sm:py-28 px-4 sm:px-6 bg-gradient-to-b from-zinc-950 to-[#050508]" data-testid="snapshot-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-full px-4 py-2 mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </span>
              <span className="text-sm text-zinc-400">Live Feed Preview</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-zinc-100 mb-4">
              What People Are Reporting Right Now
            </h2>
            <p className="text-zinc-500 text-sm">
              Real signals from the network • Updated continuously
            </p>
          </div>

          {/* Live signals grid */}
          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 animate-pulse">
                  <div className="h-4 bg-zinc-800 rounded w-1/4 mb-3" />
                  <div className="h-4 bg-zinc-800 rounded w-full mb-2" />
                  <div className="h-4 bg-zinc-800 rounded w-3/4" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {liveSignals.map((signal, index) => (
                <LiveSignalCard key={signal.id} signal={signal} index={index} />
              ))}
            </div>
          )}

          {/* More signals indicator */}
          <div className="text-center mt-8">
            <p className="text-zinc-600 text-sm mb-4">
              + hundreds more signals updated daily
            </p>
            <CTAButton onClick={onJoinClick} variant="secondary" size="small">
              <Zap className="w-4 h-4" />
              Get Full Access
            </CTAButton>
          </div>
        </div>
      </section>

      {/* ===== SECTION 5: FEATURES ===== */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 bg-[#050508]" data-testid="features-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-zinc-100 mb-4">
              Everything You Need to Stay Aware
            </h2>
            <p className="text-zinc-500 text-base max-w-xl mx-auto">
              A complete situational awareness toolkit
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FeatureCard 
              icon={Activity}
              title="Real-Time Signal Feed"
              description="Live stream of reports from across the country, filtered by what matters to you."
            />
            <FeatureCard 
              icon={MapPin}
              title="Location-Based Awareness"
              description="See what's happening in your area or track specific regions you care about."
            />
            <FeatureCard 
              icon={Sparkles}
              title='Daily "Reset" Tab'
              description="Start each day grounded with encouragement, wisdom, and clarity."
            />
            <FeatureCard 
              icon={Shield}
              title="Private Environment"
              description="No ads, no algorithms, no noise. Just signal. Members-only access."
            />
          </div>
        </div>
      </section>

      {/* ===== SECTION 6: EMOTIONAL HOOK ===== */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 bg-gradient-to-b from-[#050508] to-zinc-950" data-testid="emotional-section">
        <div className="max-w-3xl mx-auto text-center">
          <div className="w-16 h-16 bg-zinc-900 rounded-2xl flex items-center justify-center mx-auto mb-8 border border-zinc-800">
            <Heart className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-zinc-100 mb-6">
            Stay Aware. Stay Grounded.
          </h2>
          <p className="text-zinc-400 text-base sm:text-lg leading-relaxed max-w-xl mx-auto">
            Know what's happening—without losing your peace. The network keeps you informed while the "Reset" tab keeps you centered.
          </p>
        </div>
      </section>

      {/* ===== SECTION 7: PRICING / CTA ===== */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 bg-zinc-950" data-testid="pricing-section">
        <div className="max-w-xl mx-auto">
          <div className="relative bg-gradient-to-b from-zinc-900 to-zinc-900/50 border border-zinc-800 rounded-2xl p-8 sm:p-10 text-center overflow-hidden">
            {/* Background glow */}
            <div className="absolute inset-0 bg-gradient-to-b from-red-500/5 to-transparent pointer-events-none" />
            
            <div className="relative">
              <span className="inline-block bg-red-500/10 text-red-400 text-xs font-semibold px-3 py-1 rounded-full mb-6 border border-red-500/20">
                FOUNDING MEMBERS
              </span>
              
              <h2 className="text-2xl sm:text-3xl font-bold text-zinc-100 mb-2">
                Get Instant Access
              </h2>
              
              <div className="flex items-baseline justify-center gap-1 mb-6">
                <span className="text-4xl sm:text-5xl font-bold text-white">$7</span>
                <span className="text-zinc-500">/month</span>
              </div>
              
              <ul className="text-left text-zinc-400 text-sm space-y-3 mb-8 max-w-xs mx-auto">
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span>Full access to all signals</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span>Daily intelligence briefings</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span>Submit your own reports</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span>Daily Reset for grounding</span>
                </li>
              </ul>
              
              <CTAButton onClick={onJoinClick} size="large" className="w-full glow-red" data-testid="pricing-cta-btn">
                Get Instant Access
              </CTAButton>
              
              <p className="text-zinc-600 text-xs mt-4">
                Cancel anytime • No questions asked
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 8: FINAL CTA ===== */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 bg-[#050508] border-t border-zinc-900" data-testid="final-cta-section">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-zinc-100 mb-6">
            You either stay ahead…
            <br />
            <span className="text-zinc-500">or catch up later.</span>
          </h2>
          
          <CTAButton onClick={onJoinClick} size="large" data-testid="final-cta-btn">
            Join Now
          </CTAButton>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 bg-[#050508] border-t border-zinc-900">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Radio className="w-4 h-4 text-red-500" />
            <span className="text-zinc-500 text-sm">InSpiteOf Private Signal Network</span>
          </div>
          <p className="text-zinc-600 text-xs">
            © 2024 InSpiteOf. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
